import { useState, useEffect, useCallback } from 'react';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, ArrowLeft, Save, RotateCcw, Check, AlertCircle, User, Building2, Phone, Image } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useTenant } from '../context/TenantContext';
import { getProfile, upsertProfile, type AdvisorProfile } from '../lib/advisorProfile';

const EMPTY_FORM: Omit<AdvisorProfile, 'location_id'> = {
  advisor_name: '',
  advisor_title: '',
  credentials: '',
  company_name: '',
  advisor_email: '',
  advisor_phone: '',
  website: '',
  bio: '',
  advisor_headshot: '',
  company_logo: '',
  cta_link: '',
};

type FormData = typeof EMPTY_FORM;

import { ImagePreview } from '../components/ui/ImagePreview';

function InputField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  preview,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  type?: string;
  placeholder?: string;
  preview?: boolean;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-300">{label}</label>
      <div className="relative">
        <motion.div
          className="absolute inset-0 rounded-xl -z-10"
          animate={{
            boxShadow: focused
              ? '0 0 0 2px rgba(37, 99, 235, 0.3)'
              : '0 0 0 0px rgba(37, 99, 235, 0)',
          }}
          transition={{ duration: 0.2 }}
        />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 transition-colors duration-200 focus:outline-none focus:border-primary-500/50"
        />
      </div>
      {preview && <ImagePreview url={value} alt={label} />}
    </div>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-300">{label}</label>
      <div className="relative">
        <motion.div
          className="absolute inset-0 rounded-xl -z-10"
          animate={{
            boxShadow: focused
              ? '0 0 0 2px rgba(37, 99, 235, 0.3)'
              : '0 0 0 0px rgba(37, 99, 235, 0)',
          }}
          transition={{ duration: 0.2 }}
        />
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={rows}
          placeholder={placeholder}
          className="w-full px-4 py-3 rounded-xl bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-slate-100 placeholder:text-slate-500 resize-y transition-colors duration-200 focus:outline-none focus:border-primary-500/50"
        />
      </div>
    </div>
  );
}

export function Settings() {
  const navigate = useNavigateWithParams();
  const { locationId } = useTenant();

  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM });
  const [savedForm, setSavedForm] = useState<FormData>({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const profile = await getProfile(locationId);
        if (profile) {
          const { location_id: _, ...rest } = profile;
          const formData: FormData = { ...EMPTY_FORM };
          for (const key of Object.keys(EMPTY_FORM) as (keyof FormData)[]) {
            if (rest[key] != null) formData[key] = rest[key];
          }
          setForm(formData);
          setSavedForm(formData);
        }
      } catch {
        showToast('error', 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [locationId, showToast]);

  const updateField = (key: keyof FormData) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsertProfile({ location_id: locationId, ...form });
      setSavedForm({ ...form });
      showToast('success', 'Profile saved successfully');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setForm({ ...savedForm });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-slate-400 text-lg"
        >
          Loading profile...
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto space-y-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-primary-400" />
            </div>
            <h1 className="text-3xl font-bold text-slate-100">Settings</h1>
          </div>
          <p className="text-slate-400 ml-[52px]">
            Your Advisor Nexus profile. Changes here apply across all Advisor Nexus tools.
          </p>
        </div>

        {/* Personal Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                <User className="w-4 h-4 text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-100">Personal Information</h2>
            </div>
            <div className="space-y-5">
              <InputField
                label="Advisor Name"
                value={form.advisor_name}
                onChange={updateField('advisor_name')}
                placeholder="John Smith"
              />
              <InputField
                label="Title"
                value={form.advisor_title}
                onChange={updateField('advisor_title')}
                placeholder="Financial Advisor"
              />
              <InputField
                label="Credentials"
                value={form.credentials}
                onChange={updateField('credentials')}
                placeholder="CFP, ChFC, CLU"
              />
              <TextAreaField
                label="Bio"
                value={form.bio}
                onChange={updateField('bio')}
                rows={4}
                placeholder="A brief bio about your practice and expertise..."
              />
            </div>
          </Card>
        </motion.div>

        {/* Company */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-100">Company</h2>
            </div>
            <div className="space-y-5">
              <InputField
                label="Company Name"
                value={form.company_name}
                onChange={updateField('company_name')}
                placeholder="Smith Financial Group"
              />
              <InputField
                label="Website"
                value={form.website}
                onChange={updateField('website')}
                placeholder="https://www.example.com"
              />
            </div>
          </Card>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                <Phone className="w-4 h-4 text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-100">Contact</h2>
            </div>
            <div className="space-y-5">
              <InputField
                label="Email"
                value={form.advisor_email}
                onChange={updateField('advisor_email')}
                type="email"
                placeholder="john@example.com"
              />
              <InputField
                label="Phone"
                value={form.advisor_phone}
                onChange={updateField('advisor_phone')}
                placeholder="(555) 123-4567"
              />
              <InputField
                label="Calendar Link"
                value={form.cta_link}
                onChange={updateField('cta_link')}
                placeholder="Your Calendly or booking page URL"
              />
            </div>
          </Card>
        </motion.div>

        {/* Images */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                <Image className="w-4 h-4 text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-100">Images</h2>
            </div>
            <div className="space-y-5">
              <InputField
                label="Headshot URL"
                value={form.advisor_headshot}
                onChange={updateField('advisor_headshot')}
                placeholder="https://example.com/headshot.jpg"
                preview
              />
              <InputField
                label="Company Logo URL"
                value={form.company_logo}
                onChange={updateField('company_logo')}
                placeholder="https://example.com/logo.png"
                preview
              />
            </div>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex gap-4 pb-8"
        >
          <Button
            onClick={handleSave}
            isLoading={saving}
            disabled={!isDirty}
            className="flex-1"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </Button>
          <Button
            variant="secondary"
            onClick={handleDiscard}
            disabled={!isDirty}
          >
            <RotateCcw className="w-4 h-4" />
            Discard
          </Button>
        </motion.div>
      </motion.div>

      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 50, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 50 }}
          className={`fixed bottom-6 left-1/2 px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg ${
            toast.type === 'success'
              ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-300'
              : 'bg-red-500/20 border border-red-500/30 text-red-300'
          }`}
        >
          {toast.type === 'success' ? (
            <Check className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {toast.message}
        </motion.div>
      )}
    </div>
  );
}
